import { useCallback, useEffect, useRef } from 'react';

/**
 * Commit-on-lift for touch drawing (#747).
 *
 * Click-to-place drawing tools were built for the mouse: a point lands on
 * mouse:down. Touch has no hover, so on a tablet you cannot position a point by
 * dragging before it is placed — it snaps to wherever the finger first touches.
 * This hook defers placement to lift (mouse:up) so the point follows the finger
 * and lands where it is released.
 *
 * Wiring (per tool):
 *  - In the mouse:down handler, after computing the snapped point, call
 *    `deferIfTouch(domEvent, snappedPoint)`. If it returns true, seed the preview
 *    and return without placing.
 *  - In the mouse:move handler, call `updatePoint(snappedPoint)` so the lift
 *    target follows the finger.
 *  - Pass the tool's placement function as `onCommit`; the hook calls it with the
 *    last snapped point on mouse:up. A second finger (pinch) cancels the pending
 *    point.
 *
 * The mouse path is untouched: `deferIfTouch` returns false for mouse/pen, so the
 * caller places immediately as before.
 *
 * @param {import('fabric').Canvas | null | undefined} canvas
 * @param {(snappedPoint: {x: number, y: number}) => void} onCommit
 * @param {() => void} [onCancel] called when a pinch cancels a pending point, so
 *   the tool can clear its dangling preview edge.
 * @returns {{deferIfTouch: (domEvent: Event, snappedPoint: object) => boolean, updatePoint: (snappedPoint: object) => void}}
 */
export function useTouchDeferredVertex(canvas, onCommit, onCancel) {
  const pendingRef = useRef(false);
  const pointRef = useRef(null);

  const deferIfTouch = useCallback((domEvent, snappedPoint) => {
    const isTouch =
      domEvent?.pointerType === 'touch' ||
      (typeof TouchEvent !== 'undefined' && domEvent instanceof TouchEvent);
    if (!isTouch) return false;
    pendingRef.current = true;
    pointRef.current = snappedPoint;
    return true;
  }, []);

  const updatePoint = useCallback((snappedPoint) => {
    if (pendingRef.current) pointRef.current = snappedPoint;
  }, []);

  useEffect(() => {
    if (!canvas) return undefined;

    const handleMouseUp = () => {
      if (!pendingRef.current) return;
      pendingRef.current = false;
      const point = pointRef.current;
      if (point) onCommit(point);
    };
    canvas.on('mouse:up', handleMouseUp);

    // A second finger starts a pinch-zoom — drop the pending point and let the
    // tool clear its preview, so the gesture neither places a stray vertex nor
    // leaves a dangling preview edge behind (#747).
    const upper = canvas.upperCanvasEl;
    const handleTouchStart = (e) => {
      if ((e.touches?.length ?? 0) >= 2 && pendingRef.current) {
        pendingRef.current = false;
        onCancel?.();
      }
    };
    upper?.addEventListener('touchstart', handleTouchStart, { passive: true });

    return () => {
      canvas.off('mouse:up', handleMouseUp);
      upper?.removeEventListener('touchstart', handleTouchStart);
    };
  }, [canvas, onCommit, onCancel]);

  return { deferIfTouch, updatePoint };
}
